'use client'
import Link from 'next/link'
import { groq } from 'next-sanity'
import React, { useEffect } from 'react'
import { useState } from 'react'
import Heading from '~/app/components/atoms/Heading'
import { client } from '~/app/lib/sanity.client'
import { FooterType, getFooter } from '~/app/lib/sanity.queries'
import Icon from '../atoms/Icons'
import Logo from '../atoms/Logo'
import Button from '../atoms/Button'
import { sanitizeString } from '~/app/utils/sanitizeString'

/**
 *
 * @returns: En footer, der viser virksomhedsoplysninger og links til sociale medier.
 * @example: <Footer />
 * @alias: Footer
 * @module: components/Footer
 * @summary: Denne komponent bruges til at vise virksomhedsoplysninger og links til sociale medier.
 * @see: src/components/Footer.tsx
 * @version: 1.0.0
 * @property: []
 * @author: Kasper Buchholtz
 *
 **/

export default function Footer() {
  const [footer, setFooter] = React.useState<FooterType[]>([])
  const [footerScripts, setFooterScripts] = useState('')
  useEffect(() => {
    getFooter().then((nav) => setFooter(nav))
    const fetchFooterScripts = async () => {
      const query = groq`*[_type == "settings" && title == "Sideinstillinger"][0]{footerScripts}`
      const result = await client.fetch(query)
      setFooterScripts(result?.footerScripts)
    }

    fetchFooterScripts()
  }, [])

  const data = footer[0]
  const address = data?.object?.address
  const address2 = data?.object?.address2
  const cvr = data?.object?.cvr
  const phone = data?.object?.telephone
  const email = data?.object?.email
  const social = data?.social
  const shortcuts = data?.shortcuts
  const companyName = data?.object?.companyName

  if (!data) {
    return null // or render a loading state
  }

  const gridCols =
    'xs:grid-cols-4 sm:grid-cols-8 md:grid-cols-12 lg:grid-cols-12 xl:grid-cols-24 2xl:grid-cols-24'
  const gridGap = 'gap-4 xs:gap-4 sm:gap-4 md:gap-6 lg:gap-6 xl:gap-6 2xl:gap-6'
  const paddingX = 'px-4 xs:px-4 sm:px-13 md:px-24 lg:px-19 xl:px-36 2xl:px-52'
  const paddingY = 'pt-[15vw] sm:pt-[5vw]'

  return (
    <>
      <footer
        className={`${gridCols} ${gridGap} ${paddingX} ${paddingY} grid pb-5 text-dark prose-headings:text-DabraRød bg-lys *:mx-auto md:*:mx-0`}
      >
        {/* Column one */}
        <div className="col-span-full sm:col-span-full md:col-span-3 lg:col-span-3 xl:col-span-6">
          <div className="">
            <Link className="text-DabraRød" href="/">
              <Logo className="w-full h-auto max-w-56" />
            </Link>
          </div>
          <ul className="flex flex-wrap justify-center mx-auto mt-8 max-w-64 md:mx-0 gap-x-4 gap-y-2 md:justify-start">
            <li>Følg os her:</li>
            {social?.map((item, index) => (
              <li key={index}>
                <Link
                  className=" hover:fill-rød fill-DabraRød transition-colors"
                  href={item.url}
                  target="_blank"
                  rel="noreferrer"
                >
                  <Icon
                    className=" size-5"
                    type={sanitizeString(item.platform)}
                  />
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Column two */}
        <div className=" col-span-full sm:col-span-full md:col-span-3 lg:col-span-3 xl:col-span-6">
          <div className="pb-4 ">
            <Heading
              className="text-center text-light-light md:text-left"
              spacing="none"
              tag="h5"
              type="h5"
            >
              Adresser
            </Heading>
          </div>
          <div className="space-y-2 font-light text-center md:text-left">
            <p>{address}</p>
            <p>{address2}</p>
            <p>{cvr}</p>
          </div>
        </div>

        {/* Column Three */}
        <div className=" col-span-full sm:col-span-full md:col-span-3 lg:col-span-3 xl:col-span-6">
          <div className="pb-4 ">
            <Heading
              className="text-center text-light-light md:text-left"
              spacing="none"
              tag="h5"
              type="h5"
            >
              Kontakt
            </Heading>
          </div>
          <div className="*:block space-y-2 text-center md:text-left font-light">
            <a href={`tel:${phone}`}>{phone}</a>
            <a href={`mailto:${email}`}>{email}</a>
          </div>
        </div>

        {/* Column Four */}
        <div className=" col-span-full sm:col-span-full md:col-span-3 lg:col-span-3 xl:col-span-6">
          <div className="pb-4 ">
            {/*  <Heading
              className="text-center text-light-light md:text-left"
              spacing="none"
              tag="h5"
              type="h5"
            >
              Genveje
            </Heading>*/}
          </div>

          <ul className="space-y-2 text-center md:text-left">
            {shortcuts?.map((item, listIndex) => (
              <li className="text-center md:text-left" key={listIndex}>
                <Button
                  style="none"
                  className="h-auto px-0 py-0 font-light"
                  link={item.link}
                >
                  {item.link.label}
                </Button>
              </li>
            ))}
          </ul>
        </div>
        <div className="mt-4 sm:mt-8 md:mt-12 lg:mt-16 col-span-full">
          <div className="flex flex-col justify-center gap-2 text-center sm:text-left sm:flex-row sm:justify-between text-[12px] text-DabraRød/60 w-full font-heading">
            <p className="text-[12px] sm:w-1/3">{`© ${new Date().getFullYear()} ${companyName}`}</p>
            <p className="text-[12px] sm:w-1/3 text-center">
              <a href="/cookiepolitik" rel="noreferrer">
                Cookiepolitik
              </a>
            </p>
            <p className="relative transition-all text-[12px] sm:w-1/3 group md:text-right">
              <a
                className="before:absolute before:-right-[11px] before:rounded-full before:animate-bounce duration-1000 before:bottom-[5px] before:size-[7px] before:block before:transition-all ease-linear transition-all group-hover:before:bg-green"
                href="https://superego.nu/kontakt/holstebro"
                target="_blank"
                rel="noreferrer"
              >
                Website by
                <span className="transition-all decoration-green ">
                  &nbsp;Superego
                </span>
              </a>
            </p>
          </div>
        </div>
      </footer>
      {/* <Script id="footerScripts">{footerScripts}</Script> */}
    </>
  )
}
